import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  MapPin, ChevronRight, Loader2, Dog, Heart, Shield, Zap,
  Clock, Ruler, Weight, Globe, Users, Baby, ArrowRight,
  Star, Plus, ExternalLink, HelpCircle, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import ApisAIFooter from '@/components/ApisAIFooter';
import SearchBar from '@/components/SearchBar';
import TopNavBar from '@/components/TopNavBar';
import AddDropdown from '@/components/AddDropdown';
import FilterBarBreadcrumb from '@/components/FilterBarBreadcrumb';
import OrganizationBadge from '@/components/OrganizationBadge';
import OrganizationFilter from '@/components/OrganizationFilter';
import { deriveOrgCode } from '@/utils/organizations';
import { ORG_FILTER_GROUPS } from '@/utils/organizations';
import { getImageUrl, PLACEHOLDER_IMAGE } from '@/utils/imageUrl';

import { API } from '@/utils/api';
import { useTranslation } from 'react-i18next';
const BASE_URL = 'https://apisai.pl';

// Animated section wrapper
const Section = ({ children, className = "", delay = 0, id }) => (
  <motion.section
    id={id}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.6, delay }}
    className={className}
  >
    {children}
  </motion.section>
);

// Stat card
const StatCard = ({ icon: Icon, label, value, color = "blue", href, emptyCta }) => {
  const colors = {
    blue: "from-blue-500/20 to-blue-600/10 border-blue-500/20 text-blue-400",
    emerald: "from-emerald-500/20 to-emerald-600/10 border-emerald-500/20 text-emerald-400",
    amber: "from-amber-500/20 to-amber-600/10 border-amber-500/20 text-amber-400",
    violet: "from-orange-500/20 to-orange-600/10 border-orange-500/20 text-orange-400",
  };
  const isEmpty = value === 0 || value === '0';
  const content = (
    <>
      <Icon className="w-6 h-6 mx-auto mb-2" />
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-gray-400 mt-1">{label}</p>
      {href && isEmpty && emptyCta && (
        <p className="text-[11px] text-white/70 mt-2 underline underline-offset-2">{emptyCta}</p>
      )}
      {href && !isEmpty && (
        <p className="text-[11px] text-white/60 mt-2">Zobacz →</p>
      )}
    </>
  );
  const base = `bg-gradient-to-br ${colors[color]} border rounded-2xl p-5 text-center`;
  if (href) {
    return (
      <a
        href={href}
        data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
        className={`${base} block transition-all duration-200 hover:scale-[1.03] hover:border-white/30 hover:shadow-lg cursor-pointer`}
      >
        {content}
      </a>
    );
  }
  return <div className={base}>{content}</div>;
};

// FAQ accordion item
const FaqItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border border-white/10 rounded-xl overflow-hidden" data-testid="faq-item">
    <button 
      className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
      onClick={onClick}
    >
      <span className="text-white font-medium pr-4">{question}</span>
      <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    {isOpen && (
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        className="px-5 pb-5"
      >
        <p className="text-gray-400 leading-relaxed">{answer}</p>
      </motion.div>
    )}
  </div>
);

function breedSectionSeo(breedName, section) {
  const map = {
    hodowle: {
      title: `Hodowle ${breedName} w Polsce — lista hodowli FCI/ZKwP | ApisAI`,
      description: `Sprawdzone hodowle ${breedName} w Polsce. Przeglądaj hodowców z rodowodem, zobacz dostępne mioty i skontaktuj się bezpośrednio.`,
    },
    mioty: {
      title: `Mioty ${breedName} — szczenięta na sprzedaż w Polsce | ApisAI`,
      description: `Aktualne mioty ${breedName} i szczenięta na sprzedaż w Polsce. Znajdź szczeniaka z rodowodem od sprawdzonej hodowli.`,
    },
    reproduktory: {
      title: `Reproduktory ${breedName} — psy do krycia w Polsce | ApisAI`,
      description: `Reproduktory ${breedName} z badaniami zdrowotnymi i tytułami. Znajdź psa ${breedName} do krycia suki.`,
    },
  };
  return map[section] || null;
}

export default function BreedPage() {
  const { t } = useTranslation();
  const { breed, section } = useParams();
  const navigate = useNavigate();
  const VALID_SECTIONS = ['hodowle', 'mioty', 'reproduktory'];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(0);
  const [activeTab, setActiveTab] = useState(['hodowle', 'mioty', 'reproduktory'].includes(section) ? section : 'mioty'); // sterowane URL /rasy/{breed}/{section}
  const [similarBreeds, setSimilarBreeds] = useState([]);
  const [notifyOk, setNotifyOk] = useState(false);
  const [orgFilter, setOrgFilter] = useState('all'); // 'all' | 'fci' | 'other' | 'no_pedigree'
  const [sortBy, setSortBy] = useState('views'); // 'views' | 'newest' | 'nearest'
  const [sponsored, setSponsored] = useState([]);

  useEffect(() => {
    fetchBreed();
    axios.get(`${API}/public/rasy/${breed}/podobne?limit=4`)
      .then(res => setSimilarBreeds(res.data.breeds || []))
      .catch(() => setSimilarBreeds([]));
  }, [breed]);

  // Pobierz sponsorowane z unified catalog API (per rasa)
  useEffect(() => {
    if (!breed) return;
    // Decode "yorkshire-terrier" → "Yorkshire Terrier" dla backendu
    const rasaName = breed.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    axios.get(`${API}/public/catalog`, { params: { rasa: rasaName, per_page: 3 } })
      .then(res => setSponsored(res.data?.sponsored || []))
      .catch(() => setSponsored([]));
  }, [breed]);

  // Inject JSON-LD schemas via useEffect (Helmet doesn't properly inject script tags)
  useEffect(() => {
    if (!data?.seo) return;
    
    // Helper to inject/update a schema script
    const injectSchema = (id, schema) => {
      if (!schema) return;
      let script = document.querySelector(`script[data-schema="${id}"]`);
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-schema', id);
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(schema);
    };

    // Inject all three schemas
    injectSchema('breed-article', data.seo.breed_schema);
    injectSchema('breed-faq', data.seo.faq_schema);
    injectSchema('breed-itemlist', data.seo.itemlist_schema);

    // Breadcrumb schema (Sprint #3) — z uwzględnieniem podstrony sekcji
    const labels = { hodowle: 'Hodowle', mioty: 'Mioty', reproduktory: 'Reproduktory' };
    const crumbs = [
      { "@type": "ListItem", position: 1, name: "APISAI", item: "https://apisai.pl" },
      { "@type": "ListItem", position: 2, name: "Rasy psów", item: "https://apisai.pl/rasy" },
      { "@type": "ListItem", position: 3, name: data.breed, item: `https://apisai.pl/rasy/${breed}` },
    ];
    if (labels[section]) {
      crumbs.push({ "@type": "ListItem", position: 4, name: `${labels[section]} ${data.breed}`, item: `https://apisai.pl/rasy/${breed}/${section}` });
    }
    injectSchema('breed-breadcrumb', {
      "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: crumbs,
    });

    // SEO meta + canonical — wstrzykiwane bezpośrednio (Helmet bywa zawodny dla <link>/<meta>)
    const _sec = breedSectionSeo(data.breed, section);
    const _t = _sec ? _sec.title : (data.seo?.title || `${data.breed} - Hodowle i Szczenięta | ApisAI`);
    const _d = _sec ? _sec.description : (data.seo?.description || `Znajdź hodowlę ${data.breed} w Polsce`);
    const cpath = labels[section] ? `/rasy/${breed}/${section}` : `/rasy/${breed}`;
    document.title = _t;
    const setMeta = (sel, attr, val, content) => {
      let el = document.querySelector(sel);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, val); el.setAttribute('data-breed-seo', '1'); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('meta[name="description"]', 'name', 'description', _d);
    setMeta('meta[property="og:title"]', 'property', 'og:title', _t);
    setMeta('meta[property="og:description"]', 'property', 'og:description', _d);
    setMeta('meta[property="og:url"]', 'property', 'og:url', `${BASE_URL}${cpath}`);
    let canon = document.querySelector('link[rel="canonical"][data-breed-seo]');
    if (!canon) {
      canon = document.createElement('link');
      canon.rel = 'canonical';
      canon.setAttribute('data-breed-seo', '1');
      document.head.appendChild(canon);
    }
    canon.href = `${BASE_URL}${cpath}`;

    // Cleanup on unmount
    return () => {
      document.querySelectorAll('script[data-schema^="breed-"]').forEach(s => s.remove());
      document.querySelectorAll('[data-breed-seo]').forEach(s => s.remove());
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, section, breed]);

  // Sync zakładki z URL (/rasy/{breed}/{section}) + nawigacja crawlowalna
  useEffect(() => {
    setActiveTab(VALID_SECTIONS.includes(section) ? section : 'mioty');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  const goTab = (tab) => {
    setActiveTab(tab);
    navigate(`/rasy/${breed}/${tab}`);
  };

  const fetchBreed = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/public/rasy/${breed}`);
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center">
        <div className="text-center">
          <Dog className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Nie znaleziono rasy</h1>
          <Link to="/" className="text-blue-400 hover:underline">Wróć do strony głównej</Link>
        </div>
      </div>
    );
  }

  const { breed: breedName, description: desc, kennels, litters, seo, stats } = data;
  const faq = desc?.faq || [];

  // SEO per-sekcja (Sprint #3) — crawlowalne /rasy/{breed}/{hodowle|mioty|reproduktory}
  const activeSection = VALID_SECTIONS.includes(section) ? section : null;
  const _secSeo = breedSectionSeo(breedName, section);
  const pageTitle = _secSeo ? _secSeo.title : (seo?.title || `${breedName} - Hodowle i Szczenięta | ApisAI`);
  const availableLitters = litters?.filter(l => l.status === 'available' || l.status === 'planned') || [];

  // Helper: filtruj i sortuj encje wg wybranego rodowodu
  const orgGroup = ORG_FILTER_GROUPS.find(g => g.key === orgFilter);
  const filterByOrg = (items) => {
    if (!items) return [];
    if (orgFilter === 'all') return items;
    const allowed = new Set(orgGroup?.codes || []);
    return items.filter(i => allowed.has(deriveOrgCode(i)));
  };
  const sortItems = (items) => {
    // Najpierw FCI-first tier, potem wybrany sort
    return [...items].sort((a, b) => {
      const tierA = deriveOrgCode(a) === 'fci_zkwp' ? 0 : deriveOrgCode(a) === 'unknown' ? 2 : 1;
      const tierB = deriveOrgCode(b) === 'fci_zkwp' ? 0 : deriveOrgCode(b) === 'unknown' ? 2 : 1;
      if (tierA !== tierB) return tierA - tierB;
      if (sortBy === 'views') return (b.views_total || 0) - (a.views_total || 0);
      if (sortBy === 'newest') {
        const ta = new Date(a.created_at || 0).getTime();
        const tb = new Date(b.created_at || 0).getTime();
        return tb - ta;
      }
      return 0;
    });
  };
  const filteredLitters = sortItems(filterByOrg(litters));
  const filteredKennels = sortItems(filterByOrg(kennels));

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta property="og:type" content="article" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#0a0f1e] via-[#0e1628] to-[#0a0f1e] text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
        {/* ============ TOP NAV (marketplace) ============ */}
        <TopNavBar />

        {/* ============ BREADCRUMB (standard nav path) ============ */}
        <div className="pt-4">
          <div className="max-w-6xl mx-auto px-6">
            <nav className="flex items-center gap-2 text-xs text-gray-500" data-testid="breed-breadcrumb" aria-label="Ścieżka nawigacji">
              <Link to="/katalog" className="hover:text-white transition">Marketplace</Link>
              <span className="text-gray-700">/</span>
              <Link to="/rasy" className="hover:text-white transition">Rasy psów</Link>
              <span className="text-gray-700">/</span>
              {activeSection ? (
                <>
                  <Link to={`/rasy/${breed}`} className="hover:text-white transition">{breedName}</Link>
                  <span className="text-gray-700">/</span>
                  <span className="text-white">{ {hodowle:'Hodowle', mioty:'Mioty', reproduktory:'Reproduktory'}[activeSection] }</span>
                </>
              ) : (
                <span className="text-white">{breedName}</span>
              )}
            </nav>
          </div>
        </div>

        {/* ============ HERO + SEARCHBAR ============ */}
        <Section className="pb-6 pt-6">
          <div className="max-w-6xl mx-auto px-6">
            <div className="mb-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-300 text-xs mb-4" data-testid="breed-context-pill">
                <Dog className="w-3.5 h-3.5 text-blue-400" />
                Rasa psów{desc?.fci_group ? ` · ${desc.fci_group}` : ''}
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-3 tracking-tight" data-testid="breed-title">
                {activeTab === 'hodowle' && `Hodowle ${breedName} w Polsce`}
                {activeTab === 'mioty' && `Mioty ${breedName} w Polsce`}
                {activeTab === 'reproduktory' && `Reproduktory ${breedName} w Polsce`}
              </h1>
              <p className="text-base md:text-lg text-gray-400 max-w-3xl">
                {activeTab === 'hodowle' && `Znajdź sprawdzoną hodowlę ${breedName} z rodowodem FCI w swojej okolicy.`}
                {activeTab === 'mioty' && `Zobacz dostępne mioty ${breedName} i znajdź szczeniaka w swojej okolicy.`}
                {activeTab === 'reproduktory' && `Znajdź reproduktora ${breedName} z badaniami zdrowotnymi i tytułami.`}
              </p>
            </div>

            {/* Global SearchBar — prefill breed */}
            <div className="mb-5">
              <SearchBar initial={{ type: activeTab === 'mioty' ? 'szczenieta' : activeTab, breed: breedName }} lockBreed />
            </div>

            {/* Sort + Filtry (spójny wygląd z /katalog) */}
            <div className="mb-5 rounded-2xl border border-white/10 bg-black/30 p-3" data-testid="breed-sort-filter-panel">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] uppercase tracking-[0.16em] text-gray-500 mr-1">Sortuj</span>
                {[
                  { key: 'views', label: 'Najwięcej wyświetleń' },
                  { key: 'newest', label: 'Najnowsze' },
                  { key: 'nearest', label: 'Blisko mnie' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key)}
                    data-testid={`breed-sort-${opt.key}`}
                    className={`px-3 py-1.5 rounded-full border text-xs transition ${sortBy === opt.key ? 'bg-white text-black border-white font-semibold' : 'bg-white/5 text-gray-300 border-white/10 hover:bg-white/10'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {activeTab !== 'reproduktory' && (
                <div className="mt-3 pt-3 border-t border-white/5">
                  <OrganizationFilter value={orgFilter} onChange={setOrgFilter} compact />
                </div>
              )}
            </div>

            {/* Counts line (per reference) */}
            <div className="mb-4 text-sm text-gray-400" data-testid="breed-counts-line">
              <span className="text-white font-semibold">
                {activeTab === 'mioty' ? (filteredLitters?.length || 0) : activeTab === 'hodowle' ? (filteredKennels?.length || 0) : 0} wyników
              </span>
              <span className="text-gray-600 mx-2">•</span>
              <span>{stats?.kennels_count || 0} hodowli tej rasy w Polsce</span>
              <span className="text-gray-600 mx-2">•</span>
              <span>{stats?.available_litters || 0} miotów</span>
              {desc?.fci_group && (
                <>
                  <span className="text-gray-600 mx-2">•</span>
                  <span>{desc.fci_group}</span>
                </>
              )}
            </div>

            {/* TABS: Mioty | Hodowle | Reproduktory (per spec order) */}
            <div className="flex flex-wrap gap-2 border-b border-white/5 pb-2" data-testid="breed-tabs">
              <button
                onClick={() => goTab('mioty')}
                data-testid="tab-mioty"
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                  activeTab === 'mioty'
                    ? 'bg-white text-black shadow-lg font-semibold'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Baby className="w-4 h-4" /> Mioty
                <span className={`text-xs ${activeTab === 'mioty' ? 'text-black/70' : 'text-gray-500'}`}>({stats?.available_litters || 0})</span>
              </button>
              <button
                onClick={() => goTab('hodowle')}
                data-testid="tab-hodowle"
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                  activeTab === 'hodowle'
                    ? 'bg-white text-black shadow-lg font-semibold'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Users className="w-4 h-4" /> Hodowle
                <span className={`text-xs ${activeTab === 'hodowle' ? 'text-black/70' : 'text-gray-500'}`}>({stats?.kennels_count || 0})</span>
              </button>
              <button
                onClick={() => setActiveTab('reproduktory')}
                data-testid="tab-reproduktory"
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                  activeTab === 'reproduktory'
                    ? 'bg-white text-black shadow-lg font-semibold'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Heart className="w-4 h-4" /> Reproduktory
              </button>
            </div>
          </div>
        </Section>

        {/* ============ SPONSORED STRIP (po tabach, przed wynikami) ============ */}
        {sponsored.length > 0 && (
          <Section className="py-6">
            <div className="max-w-6xl mx-auto px-6">
              <div className="flex items-baseline justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-400" />
                  <h2 className="text-2xl font-bold text-white">Sponsorowane</h2>
                </div>
                <span className="text-[10px] uppercase tracking-[0.18em] text-gray-600">Reklama</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="breed-sponsored-strip">
                {sponsored.slice(0, 3).map(item => (
                  <Link
                    key={`${item.type}-${item.slug}`}
                    to={item.url}
                    data-testid={`breed-sponsored-${item.type}-${item.slug}`}
                    className="group relative rounded-2xl overflow-hidden border border-yellow-500/30 bg-[#10182B]/80 hover:border-yellow-500/60 transition-all"
                  >
                    <div className="relative aspect-[16/10] bg-[#080f1f]">
                      <img
                        src={getImageUrl(item.image) || PLACEHOLDER_IMAGE}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                      <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-yellow-400 text-black text-[10px] font-bold">Sponsorowane</span>
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 border border-white/20 text-white capitalize">
                            {item.type === 'hodowla' ? 'Hodowla' : item.type === 'miot' ? 'Miot' : 'Reproduktor'}
                          </span>
                          <OrganizationBadge code={item.organization_code} />
                        </div>
                        <p className="text-white font-bold text-lg leading-tight line-clamp-1">{item.title}</p>
                        <p className="text-gray-300 text-sm mt-0.5 flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" /> {item.subtitle}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </Section>
        )}

        {/* ============ AVAILABLE LITTERS (tab: Mioty) ============ */}
        {activeTab === 'mioty' && filteredLitters && filteredLitters.length > 0 && (
          <Section id="litters-section" className="py-16 scroll-mt-20 bg-gradient-to-b from-emerald-500/5 to-transparent">
            <div className="max-w-6xl mx-auto px-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white" data-testid="litters-section">
                    Dostępne szczenięta {breedName}
                  </h2>
                  <p className="text-gray-400 mt-1">
                    {filteredLitters.length} {filteredLitters.length === 1 ? 'miot' : 'miotów'}
                    {orgFilter !== 'all' && ` (filtr: ${ORG_FILTER_GROUPS.find(g => g.key === orgFilter)?.label})`}
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredLitters.map((litter, i) => (
                  <motion.div
                    key={litter.id || i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                  >
                    <Link 
                      to={`/miot/${litter.slug}`}
                      className="block group"
                      data-testid={`litter-card-${i}`}
                    >
                      <div className="bg-[#10182B]/80 border border-white/10 rounded-2xl overflow-hidden hover:border-emerald-500/40 transition-all hover:shadow-lg hover:shadow-emerald-500/10">
                        {/* Image */}
                        <div className="relative aspect-[16/10] overflow-hidden">
                          <img 
                            src={getImageUrl(litter.cover_image || litter.gallery?.[0]) || PLACEHOLDER_IMAGE}
                            alt={`${litter.breed} - ${litter.name}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#10182B] via-transparent to-transparent" />
                          
                          {/* Status badge */}
                          <div className={`absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-semibold ${
                            litter.status === 'available' ? 'bg-emerald-500 text-white' :
                            litter.status === 'planned' ? 'bg-blue-500 text-white' :
                            litter.status === 'reserved' ? 'bg-amber-500 text-black' :
                            'bg-gray-500 text-white'
                          }`}>
                            {litter.status === 'available' ? 'Dostępne' :
                             litter.status === 'planned' ? 'Planowane' :
                             litter.status === 'reserved' ? 'Rezerwacje' : 'Sprzedane'}
                          </div>

                          {/* Organization badge (top-left) */}
                          <div className="absolute top-3 left-3">
                            <OrganizationBadge entity={litter} />
                          </div>
                        </div>

                        {/* Content */}
                        <div className="p-5">
                          <h3 className="text-lg font-bold text-white group-hover:text-emerald-400 transition-colors mb-1">
                            {litter.name}
                          </h3>
                          <p className="text-gray-400 text-sm mb-3">{litter.kennel_name}</p>
                          
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            {litter.city && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3.5 h-3.5" />
                                {litter.city}
                              </span>
                            )}
                            {(litter.available_count > 0 || litter.puppies_count > 0) && (
                              <span className="flex items-center gap-1">
                                <Baby className="w-3.5 h-3.5" />
                                {litter.available_count || litter.puppies_count} szczeniąt
                              </span>
                            )}
                          </div>

                          <div className="mt-4 flex items-center text-emerald-400 text-sm font-medium">
                            Zobacz miot
                            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </Section>
        )}

        {/* ============ EMPTY STATE: NO LITTERS (tab: Mioty) — USER CENTRIC ============ */}
        {activeTab === 'mioty' && (!filteredLitters || filteredLitters.length === 0) && (
          <Section id="litters-section" className="py-16 scroll-mt-20 bg-gradient-to-b from-emerald-500/5 to-transparent">
            <div className="max-w-5xl mx-auto px-6" data-testid="litters-empty-state">
              <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 mb-4">
                  <Baby className="w-7 h-7 text-emerald-400" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  Nie znaleźliśmy pasujących szczeniąt
                </h2>
                <p className="text-gray-400 max-w-xl mx-auto">
                  Spróbuj zmienić rasę lub lokalizację albo ustaw powiadomienie — damy znać, gdy pojawi się pasująca oferta.
                </p>
              </div>

              {/* 3 USER-FIRST OPTIONS */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {/* 1. Zobacz hodowle */}
                <button
                  onClick={() => goTab('hodowle')}
                  data-testid="empty-option-hodowle"
                  className="group relative overflow-hidden rounded-2xl border-2 border-blue-500/30 bg-gradient-to-br from-blue-500/10 to-blue-600/5 p-6 transition-all hover:border-blue-400 hover:scale-[1.02] text-left"
                >
                  <Users className="w-8 h-8 text-blue-400 mb-3" />
                  <h3 className="text-white font-semibold text-lg mb-1">Zobacz hodowle</h3>
                  <p className="text-gray-400 text-sm">
                    Sprawdź hodowle tej rasy i zapytaj o planowane mioty — często hodowcy przyjmują rezerwacje z wyprzedzeniem.
                  </p>
                  <p className="text-xs text-blue-400 mt-3 group-hover:translate-x-1 transition-transform inline-block font-medium">
                    Przełącz na Hodowle →
                  </p>
                </button>

                {/* 2. Ustaw powiadomienie */}
                <div
                  data-testid="empty-option-notify"
                  className="group relative overflow-hidden rounded-2xl border-2 border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-amber-600/5 p-6"
                >
                  <HelpCircle className="w-8 h-8 text-amber-400 mb-3" />
                  <h3 className="text-white font-semibold text-lg mb-1">Ustaw powiadomienie</h3>
                  <p className="text-gray-400 text-sm mb-3">
                    Damy Ci znać, gdy pojawi się miot {breedName}. Bez spamu.
                  </p>
                  {notifyOk ? (
                    <p className="text-xs text-emerald-400 font-medium" data-testid="notify-ok">
                      ✓ Zapisano — powiadomimy Cię.
                    </p>
                  ) : (
                    <form
                      onSubmit={async (e) => {
                        e.preventDefault();
                        const email = e.target.elements.email.value;
                        if (!email) return;
                        try {
                          await axios.post(`${API}/public/breed-alerts`, { email, breed: breedName, breed_slug: breed });
                        } catch {}
                        setNotifyOk(true);
                      }}
                      className="flex gap-2"
                    >
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="twoj@email.pl"
                        data-testid="notify-email-input"
                        className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-gray-500 focus:border-amber-500/50 outline-none"
                      />
                      <button
                        type="submit"
                        data-testid="notify-submit-btn"
                        className="px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold whitespace-nowrap"
                      >
                        Powiadom mnie
                      </button>
                    </form>
                  )}
                </div>

                {/* 3. Zmień lokalizację */}
                <Link
                  to={`/katalog?type=mioty&rasa=${encodeURIComponent(breedName)}`}
                  data-testid="empty-option-location"
                  className="group relative overflow-hidden rounded-2xl border-2 border-purple-500/30 bg-gradient-to-br from-purple-500/10 to-purple-600/5 p-6 transition-all hover:border-purple-400 hover:scale-[1.02] block"
                >
                  <MapPin className="w-8 h-8 text-purple-400 mb-3" />
                  <h3 className="text-white font-semibold text-lg mb-1">Pokaż całą Polskę</h3>
                  <p className="text-gray-400 text-sm">
                    Sprawdź szczenięta {breedName} w innych miastach i województwach — być może są dostępne bliżej niż myślisz.
                  </p>
                  <p className="text-xs text-purple-400 mt-3 group-hover:translate-x-1 transition-transform inline-block font-medium">
                    Szukaj w całym kraju →
                  </p>
                </Link>
              </div>

              {/* SUBTLE hodowca CTA — nie dominuje */}
              <div className="border-t border-white/5 pt-6 mt-6 text-center">
                <p className="text-xs text-gray-500">
                  Hodujesz {breedName}?{' '}
                  <Link to="/wizard/miot" className="text-emerald-400 hover:underline" data-testid="subtle-cta-dodaj-miot">
                    Dodaj swój miot
                  </Link>
                  {' '}lub{' '}
                  <Link to="/wizard/reproduktor" className="text-purple-400 hover:underline" data-testid="subtle-cta-dodaj-reproduktora">
                    reproduktora
                  </Link>
                  .
                </p>
              </div>
            </div>
          </Section>
        )}

        {/* ============ BREED DESCRIPTION moved to bottom of page for SEO + better UX flow ============ */}

        {/* ============ KENNELS SECTION (tab: Hodowle) ============ */}
        {activeTab === 'hodowle' && filteredKennels && filteredKennels.length > 0 && (
          <Section id="kennels-section" className="py-16 scroll-mt-20 bg-gradient-to-b from-blue-500/5 to-transparent">
            <div className="max-w-6xl mx-auto px-6">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2" data-testid="kennels-section">
                Hodowle {breedName}
              </h2>
              <p className="text-gray-400 mb-8">
                {filteredKennels.length} {filteredKennels.length === 1 ? 'hodowla' : 'hodowli'} w Polsce
                {orgFilter !== 'all' && ` (filtr: ${ORG_FILTER_GROUPS.find(g => g.key === orgFilter)?.label})`}
              </p>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredKennels.map((kennel, i) => (
                  <motion.div
                    key={kennel.id || i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.06 }}
                  >
                    <Link 
                      to={`/hodowla/${kennel.slug}`}
                      className="block group"
                      data-testid={`kennel-card-${i}`}
                    >
                      <div className="bg-[#10182B]/80 border border-white/10 rounded-2xl overflow-hidden hover:border-blue-500/40 transition-all hover:shadow-lg hover:shadow-blue-500/10">
                        {/* Image */}
                        <div className="relative h-40 overflow-hidden">
                          <img 
                            src={getImageUrl(kennel.cover_image || kennel.gallery?.[0]) || PLACEHOLDER_IMAGE}
                            alt={kennel.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#10182B] via-[#10182B]/50 to-transparent" />
                        </div>

                        {/* Content */}
                        <div className="p-5">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h3 className="text-lg font-bold text-white group-hover:text-blue-400 transition-colors line-clamp-1">
                              {kennel.name}
                            </h3>
                            <OrganizationBadge entity={kennel} />
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                            {kennel.city && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3.5 h-3.5" />
                                {kennel.city}
                              </span>
                            )}
                            {kennel.breeds && (
                              <span className="flex items-center gap-1">
                                <Dog className="w-3.5 h-3.5" />
                                {kennel.breeds.length} {kennel.breeds.length === 1 ? 'rasa' : 'ras'}
                              </span>
                            )}
                          </div>

                          <div className="flex items-center text-blue-400 text-sm font-medium">
                            Zobacz hodowlę
                            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </Section>
        )}

        {/* ============ EMPTY STATE: NO KENNELS (tab: Hodowle) — USER CENTRIC ============ */}
        {activeTab === 'hodowle' && (!filteredKennels || filteredKennels.length === 0) && (
          <Section className="py-16 bg-gradient-to-b from-blue-500/5 to-transparent">
            <div className="max-w-5xl mx-auto px-6" data-testid="kennels-empty-state">
              <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-blue-500/10 border border-blue-500/30 mb-4">
                  <Users className="w-7 h-7 text-blue-400" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  Brak hodowli {breedName} w tym widoku
                </h2>
                <p className="text-gray-400 max-w-xl mx-auto">
                  Ale możesz: sprawdzić mioty, rozszerzyć wyszukiwanie albo zobaczyć podobne rasy.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <button
                  onClick={() => goTab('mioty')}
                  data-testid="empty-hod-option-mioty"
                  className="group relative overflow-hidden rounded-2xl border-2 border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 p-6 transition-all hover:border-emerald-400 hover:scale-[1.02] text-left"
                >
                  <Baby className="w-8 h-8 text-emerald-400 mb-3" />
                  <h3 className="text-white font-semibold text-lg mb-1">Sprawdź mioty</h3>
                  <p className="text-gray-400 text-sm">Może aktualnie są dostępne szczenięta {breedName}.</p>
                  <p className="text-xs text-emerald-400 mt-3 group-hover:translate-x-1 transition-transform inline-block font-medium">Przełącz na Mioty →</p>
                </button>

                <Link
                  to={`/katalog?type=hodowle&rasa=${encodeURIComponent(breedName)}`}
                  data-testid="empty-hod-option-location"
                  className="group relative overflow-hidden rounded-2xl border-2 border-purple-500/30 bg-gradient-to-br from-purple-500/10 to-purple-600/5 p-6 transition-all hover:border-purple-400 hover:scale-[1.02] block"
                >
                  <MapPin className="w-8 h-8 text-purple-400 mb-3" />
                  <h3 className="text-white font-semibold text-lg mb-1">Pokaż całą Polskę</h3>
                  <p className="text-gray-400 text-sm">Rozszerz wyszukiwanie hodowli na inne miasta i województwa.</p>
                  <p className="text-xs text-purple-400 mt-3 group-hover:translate-x-1 transition-transform inline-block font-medium">Szukaj w całym kraju →</p>
                </Link>

                <Link
                  to={`/reproduktory/${breed}`}
                  data-testid="empty-hod-option-repro"
                  className="group relative overflow-hidden rounded-2xl border-2 border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-amber-600/5 p-6 transition-all hover:border-amber-400 hover:scale-[1.02] block"
                >
                  <Heart className="w-8 h-8 text-amber-400 mb-3" />
                  <h3 className="text-white font-semibold text-lg mb-1">Znajdź reproduktora</h3>
                  <p className="text-gray-400 text-sm">Potrzebujesz psa {breedName} do krycia suki? Sprawdź bazę.</p>
                  <p className="text-xs text-amber-400 mt-3 group-hover:translate-x-1 transition-transform inline-block font-medium">Zobacz reproduktory →</p>
                </Link>
              </div>

              <div className="border-t border-white/5 pt-6 mt-6 text-center">
                <p className="text-xs text-gray-500">
                  Hodujesz {breedName}?{' '}
                  <Link to={`/dodaj-hodowle?rasa=${encodeURIComponent(breedName)}`} className="text-blue-400 hover:underline">Dodaj swoją hodowlę</Link>
                  {' '}— darmowa rejestracja, widoczna dla tysięcy klientów.
                </p>
              </div>
            </div>
          </Section>
        )}

        {/* ============ REPRODUKTORY (tab: Reproduktory) ============ */}
        {activeTab === 'reproduktory' && (
          <Section className="py-16 bg-gradient-to-b from-purple-500/5 to-transparent">
            <div className="max-w-4xl mx-auto px-6 text-center" data-testid="reproduktory-tab-content">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-purple-500/10 border border-purple-500/30 mb-5">
                <Heart className="w-8 h-8 text-purple-400" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Reproduktory {breedName}
              </h2>
              <p className="text-gray-400 max-w-xl mx-auto mb-8">
                Pełna baza reproduktorów {breedName} z rodowodem, badaniami zdrowotnymi i tytułami — otwiera się w dedykowanym widoku z filtrami (FCI, HD/ED, Tytuły).
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                <Link
                  to={`/reproduktory/${breed}`}
                  data-testid="tab-repro-view-btn"
                  className="group relative overflow-hidden rounded-2xl border-2 border-purple-500/40 bg-gradient-to-br from-purple-500/15 to-purple-600/5 p-6 transition-all hover:border-purple-400 hover:scale-[1.02] hover:shadow-lg hover:shadow-purple-500/20"
                >
                  <Heart className="w-8 h-8 text-purple-400 mb-3 mx-auto" />
                  <p className="text-white font-semibold text-lg">Zobacz bazę reproduktorów</p>
                  <p className="text-gray-400 text-sm mt-2">Przeglądaj psy {breedName} do krycia z pełnymi filtrami.</p>
                  <p className="text-xs text-purple-400 mt-3 group-hover:translate-x-1 transition-transform inline-block">Przejdź do bazy →</p>
                </Link>

                <Link
                  to="/wizard/reproduktor"
                  data-testid="tab-repro-add-btn"
                  className="group relative overflow-hidden rounded-2xl border-2 border-amber-500/40 bg-gradient-to-br from-amber-500/15 to-amber-600/5 p-6 transition-all hover:border-amber-400 hover:scale-[1.02] hover:shadow-lg hover:shadow-amber-500/20"
                >
                  <Plus className="w-8 h-8 text-amber-400 mb-3 mx-auto" />
                  <p className="text-white font-semibold text-lg">+ Dodaj reproduktora</p>
                  <p className="text-gray-400 text-sm mt-2">Masz samca FCI {breedName}? Otwórz się na zapytania o krycie.</p>
                  <p className="text-xs text-amber-400 mt-3 group-hover:translate-x-1 transition-transform inline-block">Dodaj psa →</p>
                </Link>
              </div>
            </div>
          </Section>
        )}

        {/* ============ PODOBNE RASY (same grupa FCI) ============ */}
        {similarBreeds.length > 0 && (
          <Section className="py-12 bg-gradient-to-b from-transparent via-blue-500/[0.03] to-transparent">
            <div className="max-w-5xl mx-auto px-6">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-2xl font-bold text-white mb-2" data-testid="similar-breeds-heading">
                  Podobne rasy z tej samej grupy FCI
                </h2>
                <p className="text-gray-500 text-sm">
                  Jeśli {breedName} Ci się podoba, rozważ też:
                </p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {similarBreeds.map(sb => (
                  <Link
                    key={sb.slug}
                    to={`/rasy/${sb.slug}`}
                    data-testid={`similar-breed-${sb.slug}`}
                    className="group relative overflow-hidden rounded-xl border border-white/10 bg-[#10182B]/60 hover:bg-[#10182B]/90 hover:border-blue-400/40 transition-all p-4"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-white font-medium text-sm truncate group-hover:text-blue-400 transition-colors">{sb.name}</p>
                        {sb.kennels_count > 0 ? (
                          <p className="text-xs text-emerald-400 mt-1">{sb.kennels_count} hodowli</p>
                        ) : (
                          <p className="text-xs text-gray-500 mt-1">Zobacz rasę</p>
                        )}
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-blue-400 group-hover:translate-x-1 transition-all shrink-0 mt-0.5" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </Section>
        )}

        {/* ============ CITY INTERNAL LINKING ============ */}
        <Section className="py-12 bg-gradient-to-b from-white/[0.02] to-transparent">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-xl font-bold text-white mb-2">
              Szczenięta {breedName} w miastach
            </h2>
            <p className="text-gray-500 text-sm mb-6">Znajdź hodowlę w swoim mieście</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {["Warszawa","Kraków","Wrocław","Poznań","Gdańsk","Łódź","Katowice","Szczecin","Lublin","Białystok","Bydgoszcz","Rzeszów","Toruń","Kielce","Olsztyn","Opole","Częstochowa","Radom"].map(city => {
                const cs = city.toLowerCase().replace(/ą/g,'a').replace(/ć/g,'c').replace(/ę/g,'e').replace(/ł/g,'l').replace(/ń/g,'n').replace(/ó/g,'o').replace(/ś/g,'s').replace(/ź/g,'z').replace(/ż/g,'z').replace(/[^a-z0-9]+/g,'-').replace(/-$/,'');
                return (
                  <Link key={city} to={`/szczeniaki/${breed}/${cs}`} className="flex items-center gap-2 px-4 py-3 bg-[#10182B]/60 border border-white/5 rounded-xl hover:bg-white/5 hover:border-white/20 transition-all group text-sm">
                    <MapPin className="w-3.5 h-3.5 text-gray-600 group-hover:text-emerald-400 flex-shrink-0" />
                    <span className="text-gray-400 group-hover:text-white transition-colors">{city}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </Section>

        {/* ============ FAQ SECTION ============ */}
        {faq.length > 0 && (
          <Section className="py-16">
            <div className="max-w-3xl mx-auto px-6">
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm font-medium mb-4">
                  <HelpCircle className="w-4 h-4" />
                  FAQ
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white" data-testid="faq-section">
                  Najczęstsze pytania o {breedName}
                </h2>
              </div>

              <div className="space-y-3">
                {faq.map((item, i) => (
                  <FaqItem
                    key={i}
                    question={item.question}
                    answer={item.answer}
                    isOpen={openFaq === i}
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  />
                ))}
              </div>
            </div>
          </Section>
        )}

        {/* ============ VIRAL CTA SECTION ============ */}
        <Section className="py-20">
          <div className="max-w-4xl mx-auto px-6">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600/20 via-orange-600/20 to-orange-500/20 border border-white/10 p-10 md:p-14 text-center">
              {/* Glow */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[300px] bg-blue-500/20 blur-[100px] rounded-full" />
              
              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl font-black text-white mb-4" data-testid="viral-cta">
                  Hodujesz {breedName}?
                </h2>
                <p className="text-lg text-gray-300 mb-8 max-w-xl mx-auto">
                  Dołącz do ApisAI i pokaż swoje szczenięta tysiącom potencjalnych klientów. Strona hodowli, miotu i marketing - wszystko w jednym miejscu.
                </p>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link to={`/dodaj-hodowle?rasa=${encodeURIComponent(breedName)}`}>
                    <Button 
                      className="bg-gradient-to-r from-blue-500 to-orange-500 hover:from-blue-600 hover:to-orange-600 text-white font-bold px-8 py-6 rounded-full text-base hover:shadow-lg hover:shadow-blue-500/30 transition-all"
                      data-testid="cta-add-kennel"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      Dodaj hodowlę w ApisAI
                    </Button>
                  </Link>
                  <Link to={`/dodaj-hodowle?rasa=${encodeURIComponent(breedName)}`}>
                    <Button 
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10 px-8 py-6 rounded-full text-base"
                      data-testid="cta-add-litter"
                    >
                      <Baby className="w-5 h-5 mr-2" />
                      Dodaj miot
                    </Button>
                  </Link>
                </div>

                <p className="text-gray-500 text-sm mt-6">
                  Darmowa rejestracja. Strona gotowa w 5 minut.
                </p>
              </div>
            </div>
          </div>
        </Section>

        {/* ============ BREED DESCRIPTION (moved to end of page) ============ */}
        {desc && desc.source !== 'none' && (
          <Section className="py-16 bg-gradient-to-b from-transparent to-white/[0.02]">
            <div className="max-w-4xl mx-auto px-6">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-8" data-testid="breed-description-section">
                Opis rasy {breedName}
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Character */}
                {desc.character && (
                  <div className="bg-[#10182B]/60 border border-white/10 rounded-2xl p-6 hover:border-blue-500/30 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                        <Heart className="w-5 h-5 text-blue-400" />
                      </div>
                      <h3 className="text-lg font-bold text-white">Charakter</h3>
                    </div>
                    <p className="text-gray-400 leading-relaxed">{desc.character}</p>
                  </div>
                )}

                {/* For whom */}
                {desc.for_whom && (
                  <div className="bg-[#10182B]/60 border border-white/10 rounded-2xl p-6 hover:border-emerald-500/30 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                        <Users className="w-5 h-5 text-emerald-400" />
                      </div>
                      <h3 className="text-lg font-bold text-white">Dla kogo</h3>
                    </div>
                    <p className="text-gray-400 leading-relaxed">{desc.for_whom}</p>
                  </div>
                )}

                {/* Care */}
                {desc.care && (
                  <div className="bg-[#10182B]/60 border border-white/10 rounded-2xl p-6 hover:border-amber-500/30 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                        <Star className="w-5 h-5 text-amber-400" />
                      </div>
                      <h3 className="text-lg font-bold text-white">Pielęgnacja</h3>
                    </div>
                    <p className="text-gray-400 leading-relaxed">{desc.care}</p>
                  </div>
                )}

                {/* Activity */}
                {desc.activity && (
                  <div className="bg-[#10182B]/60 border border-white/10 rounded-2xl p-6 hover:border-orange-500/30 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                        <Zap className="w-5 h-5 text-orange-400" />
                      </div>
                      <h3 className="text-lg font-bold text-white">Aktywność</h3>
                    </div>
                    <p className="text-gray-400 leading-relaxed">{desc.activity}</p>
                  </div>
                )}

                {/* Health */}
                {desc.health && (
                  <div className="bg-[#10182B]/60 border border-white/10 rounded-2xl p-6 hover:border-rose-500/30 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center">
                        <Shield className="w-5 h-5 text-rose-400" />
                      </div>
                      <h3 className="text-lg font-bold text-white">Zdrowie</h3>
                    </div>
                    <p className="text-gray-400 leading-relaxed">{desc.health}</p>
                  </div>
                )}

                {/* Quick facts */}
                <div className="bg-[#10182B]/60 border border-white/10 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                      <Dog className="w-5 h-5 text-cyan-400" />
                    </div>
                    <h3 className="text-lg font-bold text-white">Podstawowe informacje</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between py-2 border-b border-white/5">
                      <span className="text-gray-400 flex items-center gap-2"><Clock className="w-4 h-4" /> Długość życia</span>
                      <span className="text-white font-medium">{desc.lifespan || '10-14 lat'}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-white/5">
                      <span className="text-gray-400 flex items-center gap-2"><Weight className="w-4 h-4" /> Waga</span>
                      <span className="text-white font-medium">{desc.weight || 'Zależy od odmiany'}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-white/5">
                      <span className="text-gray-400 flex items-center gap-2"><Ruler className="w-4 h-4" /> Wzrost</span>
                      <span className="text-white font-medium">{desc.height || 'Zależy od odmiany'}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-white/5">
                      <span className="text-gray-400 flex items-center gap-2"><Globe className="w-4 h-4" /> Pochodzenie</span>
                      <span className="text-white font-medium">{desc.origin || 'Europa'}</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-gray-400 flex items-center gap-2"><Star className="w-4 h-4" /> Grupa FCI</span>
                      <span className="text-white font-medium text-right text-sm">{desc.fci_group || 'FCI'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Section>
        )}

        {/* ============ FOOTER ============ */}
        <ApisAIFooter pageType="breed" pageSlug={breed} />
      </div>
    </>
  );
}
